import React, { FC, useMemo } from 'react';
import { Document, Page, Text, View } from '@react-pdf/renderer';
import { displayPriceFormat } from '../../utils/tools';
import { getDateFormat } from '../../utils/suitableDate';
import {
    Invoice,
    InvoiceAccountBalanceDueLine,
    InvoiceAccountBalanceIncoming,
    InvoiceLine,
    InvoiceSection
} from '../../models/Invoice';
import { Legal } from '../../models/Legal';
import { styles } from './InvoiceDocument.styles';
import { formatAddressLines } from '../../utils/addressUtils';
import { getVatNumber, hasAnyLegalInfo } from '../../utils/LegalUtils';
import {
    getBalanceFormulaText,
    getIncomingBalanceRows,
    getVolumeLabel,
    orderLines
} from './InvoiceDocument.service';
import InvoiceDocumentFeeItemView from '../invoice_document_fee_item';
import { DRIVER, NETWORK, ORDER_REFUND, ORDER_TYPE_DELIVERY, PAYMENT_TYPE_CONTRACT } from '../../constants';
import { capitalize } from '@material-ui/core';

type Props = {
    invoice: Invoice;
    isNetwork: boolean;
};

const InvoiceDocumentView: FC<Props> = ({ invoice, isNetwork }) => {
    const pages = useMemo(() => {
        const serviceBillings = invoice?.serviceBillings ?? {};

        return Object.entries(serviceBillings)
            .map(([creditorKey, sb]) => {
                const sections = orderLines(sb?.sections ?? [])
                    .filter((s) => (s?.lines?.length ?? 0) > 0)
                    .filter((s) => s.key !== ORDER_REFUND && s.type !== DRIVER);
                return {
                    creditorKey,
                    creditor: sb?.creditor ?? creditorKey,
                    sections
                };
            })
            .filter((p) => p.sections.length > 0);
    }, [invoice?.serviceBillings]);

    const hasAnyPage = pages.length > 0;

    const renderFooter = () => (
        <Text style={[styles.bottomPage, styles.topBorder]}>
            Des questions ? Contactez Dishop à l'adresse contact@dishop.co ou contactez directement votre référent
            commercial
        </Text>
    );

    const renderBalanceLines = (dueLines: InvoiceAccountBalanceDueLine[]) =>
        (dueLines ?? []).map((l) => {
            const isRefund = l.key === ORDER_REFUND;
            const label = isRefund ? l.name : `Montant dû TTC - ${capitalize(l.name)}`;
            return (
                <View key={l.key} style={[styles.tableau, styles.rowPosition, { minWidth: '100%' }]}>
                    <Text style={styles.labelTab}>{label}</Text>
                    <Text style={styles.labelTab}>-{displayPriceFormat(l.due)}</Text>
                </View>
            );
        });

    const renderBalanceTotals = (incoming: InvoiceAccountBalanceIncoming | undefined) => {
        return getIncomingBalanceRows(incoming, isNetwork)
            .map(({ key, label, value }) => (
                <View key={key}
                    style={[styles.tableau, styles.rowPosition, { minWidth: '100%' }]}>
                    <Text style={styles.labelTab}>{label}</Text>
                    <Text style={styles.labelTab}>{displayPriceFormat(value ?? 0)}</Text>
                </View>
            ));
    };

    const renderBalancePage = () => {
        const incoming = invoice.accountBalance?.incoming;
        const dueLines = orderLines(invoice.accountBalance?.due?.lines!);
        const totalDue = invoice.accountBalance?.due?.totalDue ?? 0;
        const balance = invoice.accountBalance?.balance;

        const globalDueText = getBalanceFormulaText(isNetwork)
        return (
            <Page style={styles.body} size="A4">
                <Text style={styles.title}>Résumé du solde</Text>

                <View style={styles.positionTab2}>
                    <Text style={[styles.text, styles.ref]}>
                        <Text style={styles.underlinedText}>Objet:</Text> Fournit les informations détaillées de votre
                        solde ainsi que le solde final de compte sur la période.
                    </Text>

                    <View style={styles.positionTabValue}>
                        <View style={[styles.border, styles.row]}>
                            <Text style={styles.titleTab}>Description</Text>
                            <Text style={styles.titleTab}>Montant</Text>
                        </View>

                        {renderBalanceTotals(incoming)}

                        <View style={styles.border} />

                        {renderBalanceLines(dueLines)}

                        {dueLines?.length > 0 && (
                            <View style={[styles.tableau, styles.rowPosition, { minWidth: '100%' }]}>
                                <Text style={styles.labelTab}>Montant dû TOTAL (TTC)</Text>
                                <Text style={styles.labelTab}>-{displayPriceFormat(totalDue)}</Text>
                            </View>
                        )}

                        <View style={styles.border} />

                        <View style={[styles.tableau, styles.rowPosition]}>
                            <View>
                                <Text style={styles.labelTab}>Solde de compte *</Text>
                                <Text style={[styles.underLabel, styles.rowPosition]}>* Ce montant est calculé selon la
                                    formule suivante : {globalDueText}</Text>
                            </View>
                            <View>
                                <Text style={styles.labelTab}>{displayPriceFormat(balance ?? 0)}</Text>
                            </View>
                        </View>
                    </View>
                </View>

                {renderFooter()}
            </Page>
        );
    };

    const renderHeader = (creditorKey: string) => {
        const creditor =
            creditorKey === NETWORK ? invoice?.networkLegal : invoice?.dishopLegal;
        const debtor = isNetwork ? invoice?.networkLegal : invoice?.shopLegal;

        type RenderLegalBlockParams = {
            legal?: Legal;
            showEmail?: boolean;
            showRcs?: boolean;
            title?: string;
            alignRight?: boolean;
        };

        const renderLegalBlock = ({
            legal,
            showEmail = true,
            showRcs = true,
            title,
            alignRight
        }: RenderLegalBlockParams) => {
            if (!hasAnyLegalInfo(legal)) return null;

            const addr = formatAddressLines(legal?.address);
            const vat = getVatNumber(legal!);
            const rcs = legal?.registreCommerceNumero ?? '';

            return (
                <View style={alignRight ? styles.rightLegal : undefined}>
                    {title ? (
                        <Text style={[styles.presdishop, styles.underlinedText, styles.textStyle]}>{title}</Text>
                    ) : null}

                    {legal?.companyName ? (
                        <Text style={alignRight ? styles.nameBuis : styles.nameUs}>{legal.companyName}</Text>
                    ) : null}

                    {addr.map((l) => (
                        <Text key={l} style={styles.presdishop}>
                            {l}
                        </Text>
                    ))}

                    {showRcs && rcs ? <Text style={styles.presdishop}>{rcs}</Text> : null}
                    {vat ? <Text style={styles.presdishop}>{vat}</Text> : null}
                    {showEmail && legal?.email ? <Text style={styles.presdishop}>{legal.email}</Text> : null}
                </View>
            );
        };

        const creditorBlock = renderLegalBlock({
            legal: creditor,
            showEmail: true,
            showRcs: true,
            alignRight: false
        });

        const debtorBlock = renderLegalBlock({
            legal: debtor,
            showEmail: false,
            showRcs: true,
            title: 'Facturé à:',
            alignRight: true
        });

        if (!creditorBlock && !debtorBlock) {
            return null;
        }

        return (
            <>
                <View style={styles.companies}>
                    <View>{creditorBlock}</View>
                    <View style={styles.rightHeader}>{debtorBlock}</View>
                </View>

                <View style={[styles.ref, styles.alignementStyle]}>
                    {invoice?.ref ? (
                        <Text style={styles.text}>
                            <Text style={styles.underlinedText}>Référence de facture:</Text> {invoice.ref}
                        </Text>
                    ) : null}

                    {invoice?.period?.issueDate ? (
                        <Text style={styles.text}>
                            <Text style={styles.underlinedText}>Date d'émission:</Text> {invoice.period.issueDate}
                        </Text>
                    ) : null}

                    {invoice?.period?.startDate && invoice?.period?.endDate ? (
                        <Text style={styles.text}>
                            <Text style={styles.underlinedText}>Période de facturation:</Text>{' '}
                            {invoice.period.startDate} - {invoice.period.endDate}
                        </Text>
                    ) : null}

                    <Text style={styles.text}>
                        <Text style={styles.underlinedText}>Date de téléchargement:</Text> {getDateFormat()}
                    </Text>
                </View>
            </>
        );
    };

    const renderInvoiceObject = () => (
        <Text style={[styles.text, styles.ref]}>
            <Text style={styles.underlinedText}>Objet:</Text> Frais pour l’ensemble des services liés à la commande en
            ligne
        </Text>
    );

    const getDisplayTitle = (title: string | undefined): string => {
        if (title?.includes(PAYMENT_TYPE_CONTRACT)) {
            return title!;
        }
        return `Frais de ${title}`;
    };

    const renderSection = (section: InvoiceSection) => {
        const lines = section?.lines ?? [];

        const totalSumFees = lines.reduce((acc, l) => acc + (l.totalTtc ?? 0), 0);
        const totalHt = lines.reduce((acc, l) => acc + (l.totalHt ?? 0), 0);
        const tvaAmount = lines.reduce((acc, l) => acc + (l.vatAmount ?? 0), 0);
        const isZeroVAT = tvaAmount === 0;

        const isExternalDelivery = ORDER_TYPE_DELIVERY === section?.type;

        const title = section?.name ?? section?.key;
        const displayTitle = getDisplayTitle(title)
        const displaySubTotal = lines.length > 1;

        return (
            <View key={section.key} style={styles.contract} wrap={false}>
                <View style={[styles.border, styles.titleContract, { minWidth: '100%' }]}>
                    <Text style={[styles.titleTab, styles.titleTextContract]}>{displayTitle}</Text>
                </View>

                <View style={styles.firstTabValue}>
                    <View style={[styles.border, styles.row, styles.leftPart]}>
                        <Text
                            style={styles.titleTab}>{isExternalDelivery ? 'Détails des livraisons' : 'Description'}</Text>
                        <Text style={styles.titleTab}>Qté</Text>
                    </View>
                    <View style={[styles.border, styles.row, styles.rightPart]}>
                        <Text
                            style={styles.titleTab}>{isExternalDelivery ? '\u00A0' : 'Prix unitaire'}</Text>
                        <Text style={styles.titleTab}>Montant</Text>
                    </View>
                </View>

                {lines.map((line: InvoiceLine, i: number) => (
                    <InvoiceDocumentFeeItemView
                        key={`${section.key}-${i}`}
                        line={line}
                        isExternalDelivery={isExternalDelivery}
                        volumeLabel={getVolumeLabel(section.key)}
                        displaySubTotal={displaySubTotal}
                        sectionType={section.type}
                    />)
                )}

                <View style={styles.positionRow}>
                    <View style={styles.leftPart} />
                    <View style={styles.rightPart}>
                        <View style={[styles.tableau]}>
                            <Text style={styles.labelTab}>Total HT</Text>
                            <Text style={styles.labelTab}>{displayPriceFormat(totalHt)}</Text>
                        </View>

                        {isZeroVAT ? (
                            <View style={[styles.tableau, styles.border]}>
                                <Text style={styles.labelTab}>TVA (0 %)</Text>
                                <Text style={styles.labelTab}>{displayPriceFormat(0)}</Text>
                            </View>
                        ) : (
                            <View style={[styles.tableau, styles.border]}>
                                <Text style={styles.labelTab}>TVA (20 %)</Text>
                                <Text style={styles.labelTab}>{displayPriceFormat(tvaAmount)}</Text>
                            </View>
                        )}

                        <View style={styles.tableau}>
                            <Text style={styles.totalLabel}>Montant dû TTC de {title}</Text>
                            <Text style={styles.totalStyle}>{displayPriceFormat(totalSumFees)}</Text>
                        </View>
                    </View>
                </View>
            </View>
        );
    };

    if (!invoice || !hasAnyPage) {
        return (
            <Document title="Facture">
                <Page style={styles.body}>
                    <Text style={styles.title}>Facture</Text>
                    <Text style={styles.noInvoiceText}>Aucune ligne de facturation</Text>
                    {renderFooter()}
                </Page>
            </Document>
        );
    }

    return (
        <Document title={`Facture ${invoice.ref}`}>
            {pages.map((p) => (
                <Page key={p.creditorKey} style={styles.body} size="A4">
                    <Text style={styles.title}>Facture</Text>
                    {renderHeader(p.creditor)}
                    <View style={styles.positionTab}>
                        {renderInvoiceObject()}
                        {p.sections.map(renderSection)}
                    </View>
                </Page>
            ))}

            {renderBalancePage()}
        </Document>
    );
};

export default InvoiceDocumentView;